// apps/admin/src/lib/api.ts
import { StaffService, TestsStaffService, configureApi, setToken, getToken, setRefreshToken, getRefreshToken, clearAuth, OpenAPI } from '@tb/api-client/client';
import { reportClientError } from '@tb/ui';

const BASE = (process.env.NEXT_PUBLIC_API_URL ?? 'http://localhost:4000') + '/api';
OpenAPI.BASE = BASE;
configureApi(getToken() ?? undefined);

/** Общая обёртка: fetch + автоматический репорт ошибок в error_logs */
async function staffFetch(path: string, init: RequestInit = {}): Promise<any> {
  const method = init.method ?? 'GET';
  try {
    const r = await fetch(BASE + path, {
      ...init,
      headers: { ...init.headers, Authorization: `Bearer ${getToken() ?? ''}` },
    });
    if (!r.ok) {
      // Репортим серверные ошибки (5xx) и неожиданные 4xx (кроме 401, 404, 429)
      if (r.status >= 500 || (r.status !== 401 && r.status !== 404 && r.status !== 429 && r.status !== 304)) {
        reportClientError({
          service: 'admin',
          severity: r.status >= 500 ? 'fatal' : 'warning',
          name: `ApiError${r.status}`,
          message: `HTTP ${r.status} ${method} ${path}`,
          context: { source: 'staffFetch', method, path, statusCode: r.status },
        });
      }
      throw new Error('HTTP ' + r.status);
    }
    return r.json();
  } catch (e: any) {
    // Network error ("Failed to fetch")
    if (e?.message?.includes('Failed to fetch') || e?.name === 'TypeError') {
      reportClientError({
        service: 'admin',
        severity: 'error',
        name: 'NetworkError',
        message: e?.message ?? 'Failed to fetch',
        stack: e?.stack,
        context: { source: 'staffFetch', method, path },
      });
    }
    throw e;
  }
}

async function staffGet(path: string) {
  return staffFetch(path);
}

async function staffPatch(path: string, body: any) {
  return staffFetch(path, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

async function staffPost(path: string, body: any) {
  return staffFetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

async function staffPut(path: string, body: any) {
  return staffFetch(path, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

async function staffDelete(path: string) {
  return staffFetch(path, { method: 'DELETE' });
}

export const api = {
  // Auth
  staffLogin: (email: string, password: string) =>
    StaffService.staffAuthControllerLogin({ requestBody: { email, password } }),
  staffMe: () => StaffService.staffAuthControllerMe(),
  staffLogout: () => {
    // Отзываем refresh-токен на сервере, потом чистим локально
    const rt = getRefreshToken();
    return rt
      ? StaffService.staffAuthControllerLogout({ requestBody: { refreshToken: rt } }).catch(() => null)
      : Promise.resolve(null);
  },
  // Tests
  listTests: () => staffGet('/tests/staff/list'),
  getTest: (id: string) => staffGet(`/tests/staff/${id}`),
  createTest: (body: any) =>
    TestsStaffService.testsStaffControllerCreate({ requestBody: body }),
  addQuestion: (testId: string, body: any) =>
    TestsStaffService.testsStaffControllerAddQuestion({ id: testId, requestBody: body }),
  updateQuestion: (testId: string, qid: string, body: any) =>
    staffPatch(`/tests/staff/${testId}/questions/${qid}`, body),
  removeQuestion: (testId: string, qid: string) =>
    staffDelete(`/tests/staff/${testId}/questions/${qid}`),
  reorderQuestions: (testId: string, items: { id: string; order: number }[]) =>
    staffPatch(`/tests/staff/${testId}/reorder`, { items }),
  updateTestMeta: (testId: string, body: any) =>
    staffPatch(`/tests/staff/${testId}`, body),
  publishTest: (testId: string) =>
    TestsStaffService.testsStaffControllerPublish({ id: testId }),
  // Admin — dashboard
  dashboard: () => staffGet('/staff/admin/dashboard'),
  // Admin — companies
  listCompanies: (search?: string, status?: string, tariff?: string, page = 1, limit = 25) => {
    const p = new URLSearchParams();
    if (search) p.set('search', search);
    if (status) p.set('status', status);
    if (tariff) p.set('tariff', tariff);
    p.set('page', String(page));
    p.set('limit', String(limit));
    return staffGet(`/staff/admin/companies?${p}`);
  },
  companyDetail: (id: string) => staffGet(`/staff/admin/companies/${id}`),
  companyMembers: (id: string, search?: string, page = 1, limit = 25) => {
    const p = new URLSearchParams();
    if (search) p.set('search', search);
    p.set('page', String(page));
    p.set('limit', String(limit));
    return staffGet(`/staff/admin/companies/${id}/members?${p}`);
  },
  updateCompany: (id: string, body: { tariff?: string; status?: string; name?: string; inn?: string }) =>
    staffPatch(`/staff/admin/companies/${id}`, body),
  // Admin — users
  listUsers: (search?: string, page = 1, limit = 25) => {
    const p = new URLSearchParams();
    if (search) p.set('search', search);
    p.set('page', String(page));
    p.set('limit', String(limit));
    return staffGet(`/staff/admin/users?${p}`);
  },
  blockUser: (id: string) => staffPatch(`/staff/admin/users/${id}/block`, {}),
  unblockUser: (id: string) => staffPatch(`/staff/admin/users/${id}/unblock`, {}),
  // Admin — staff (сотрудники платформы)
  listStaff: (search?: string) => {
    const p = new URLSearchParams();
    if (search) p.set('search', search);
    return staffGet(`/staff/admin/staff?${p}`);
  },
  createStaff: (body: { email: string; password: string; fullName?: string; platformRole: string; department?: string }) =>
    staffPost('/staff/admin/staff', body),
  updateStaff: (id: string, body: { platformRole?: string; fullName?: string; department?: string; status?: string }) =>
    staffPatch(`/staff/admin/staff/${id}`, body),
  resetStaffPassword: (id: string, password: string) =>
    staffPatch(`/staff/admin/staff/${id}/reset-password`, { password }),
  // Admin — teams (команды внутри компаний)
  listTeams: (companyId: string, search?: string, page = 1, limit = 25) => {
    const p = new URLSearchParams();
    if (search) p.set('search', search);
    p.set('page', String(page));
    p.set('limit', String(limit));
    return staffGet(`/staff/admin/companies/${companyId}/teams?${p}`);
  },
  teamDetail: (companyId: string, teamId: string) => staffGet(`/staff/admin/companies/${companyId}/teams/${teamId}`),
  // Admin — billing
  listPlans: () => staffGet('/staff/admin/billing/plans'),
  updatePlan: (id: string, body: any) => staffPatch(`/staff/admin/billing/plans/${id}`, body),
  createPlan: (body: any) =>
    staffPost('/staff/admin/billing/plans', body),
  listSubscriptions: (page = 1, limit = 25, status?: string) => {
    const p = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (status && status !== 'all') p.set('status', status);
    return staffGet(`/staff/admin/billing/subscriptions?${p}`);
  },
  companyBilling: (id: string) => staffGet(`/staff/admin/billing/companies/${id}`),
  subscribe: (id: string, body: { planSlug: string; periodMonths?: number; autoRenew?: boolean; note?: string }) =>
    staffPost(`/staff/admin/billing/companies/${id}/subscribe`, body),
  // Admin — credits
  listCreditPackages: () => staffGet('/staff/admin/credits/packages'),
  updateCreditPackage: (id: string, body: any) => staffPatch(`/staff/admin/credits/packages/${id}`, body),
  createCreditPackage: (body: any) =>
    staffPost('/staff/admin/credits/packages', body),
  listBalances: (page = 1, limit = 25) => {
    const p = new URLSearchParams({ page: String(page), limit: String(limit) });
    return staffGet(`/staff/admin/credits/balances?${p}`);
  },
  getBalance: (companyId: string) => staffGet(`/staff/admin/credits/balance/${companyId}`),
  listCreditTransactions: (companyId?: string, page = 1, limit = 25) => {
    const p = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (companyId) p.set('companyId', companyId);
    return staffGet(`/staff/admin/credits/transactions?${p}`);
  },
  purchaseCredits: (companyId: string, packageId: string, note?: string) =>
    staffPost('/staff/admin/credits/purchase', { companyId, packageId, note }),
  giftCredits: (companyId: string, type: string, amount: number, note?: string) =>
    staffPost('/staff/admin/credits/gift', { companyId, type, amount, note }),
  // Admin — settings
  listSettings: () => staffGet('/staff/admin/credits/settings'),
  updateSetting: (key: string, value: string) =>
    staffPatch(`/staff/admin/credits/settings/${key}`, { value }),
  // Admin — promo codes
  listPromoCodes: (page = 1, limit = 25) => {
    const p = new URLSearchParams({ page: String(page), limit: String(limit) });
    return staffGet(`/staff/admin/promo?${p}`);
  },
  createPromoCode: (body: any) =>
    staffPost('/staff/admin/promo', body),
  updatePromoCode: (id: string, body: any) => staffPatch(`/staff/admin/promo/${id}`, body),
  deletePromoCode: (id: string) =>
    staffDelete(`/staff/admin/promo/${id}`),
  applyPromoCode: (code: string, companyId: string) =>
    staffPost('/staff/admin/promo/apply', { code, companyId }),
  // Admin — error logs
  errorLogStats: () => staffGet('/staff/admin/error-logs/stats'),
  errorLogList: (params: { page?: number; limit?: number; service?: string; severity?: string; status?: string; search?: string }) => {
    const p = new URLSearchParams();
    if (params.page) p.set('page', String(params.page));
    if (params.limit) p.set('limit', String(params.limit));
    if (params.service) p.set('service', params.service);
    if (params.severity) p.set('severity', params.severity);
    if (params.status) p.set('status', params.status);
    if (params.search) p.set('search', params.search);
    return staffGet(`/staff/admin/error-logs?${p}`);
  },
  errorLogDetail: (id: string) => staffGet(`/staff/admin/error-logs/${id}`),
  errorLogUpdateStatus: (id: string, status: string) =>
    staffPatch(`/staff/admin/error-logs/${id}`, { status }),
  errorLogBulkUpdate: (ids: string[], status: string) =>
    staffPost('/staff/admin/error-logs/bulk', { ids, status }),
  errorLogDelete: (id: string) =>
    staffDelete(`/staff/admin/error-logs/${id}`),
  errorLogCleanup: (daysOld = 30) =>
    staffPost('/staff/admin/error-logs/cleanup', { daysOld }),
  // Admin — articles
  listArticles: () => staffGet('/articles/admin/all'),
  getArticle: (id: string) => staffGet(`/articles/admin/${id}`),
  createArticle: (body: any) => staffPost('/articles/admin', body),
  updateArticle: (id: string, body: any) => staffPatch(`/articles/admin/${id}`, body),
  deleteArticle: (id: string) => staffDelete(`/articles/admin/${id}`),
  uploadArticleImage: async (file: File): Promise<string> => {
    const fd = new FormData();
    fd.append('file', file);
    const token = getToken();
    const res = await fetch(`${BASE}/articles/admin/upload`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: fd,
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json?.message ?? 'Upload failed');
    return json.data.url;
  },
  // Admin — article links
  getArticleLinks: (id: string) => staffGet(`/articles/admin/${id}/links`),
  setArticleLinks: (id: string, links: any[]) =>
    staffPut(`/articles/admin/${id}/links`, { links }),
  // Admin — article cross-links
  getCrossLinks: (id: string) => staffGet(`/articles/admin/${id}/cross-links`),
  setCrossLinks: (id: string, crossLinks: any[]) =>
    staffPut(`/articles/admin/${id}/cross-links`, { crossLinks }),
  // Admin — programs
  listPrograms: () => staffGet('/programs/admin/all'),
  getProgram: (id: string) => staffGet(`/programs/admin/${id}`),
  createProgram: (body: any) => staffPost('/programs/admin', body),
  updateProgram: (id: string, body: any) => staffPatch(`/programs/admin/${id}`, body),
  deleteProgram: (id: string) => staffDelete(`/programs/admin/${id}`),
  createProgramTask: (programId: string, body: any) => staffPost(`/programs/admin/${programId}/tasks`, body),
  updateProgramTask: (taskId: string, body: any) => staffPatch(`/programs/admin/tasks/${taskId}`, body),
  deleteProgramTask: (taskId: string) => staffDelete(`/programs/admin/tasks/${taskId}`),
};

export { setToken, getToken, setRefreshToken, getRefreshToken, clearAuth };
